import { action, makeAutoObservable, reaction } from 'mobx'
import EnekaDefinitions from '../preset.json'
import { apiCall, getDocumentAndDownload } from '../Api'
import {
  type ApiResponse,
  type ConsumptionPointData,
  type LegendElement,
  type QuarterResponse,
  type ThemeElement
} from '../Pages/MapPage/@types'
import type AppStore from './AppStore'
import { FeatureType } from './features'
import type PickInfo from '@deck.gl/core/typed/lib/deck'
import { type Form } from 'Pages/MapPage/Monitor/@types'
import { type Address } from './@types'

const wktCoordinatesSRID = 'SRID=4326'

class MapStore {
  public themeTree = EnekaDefinitions.themes
  public selectedThemesLegend: LegendElement[] = []
  public apiResponse: ApiResponse | undefined = undefined
  public consumptionPoints: any[] = []
  public lastObjectID = ''
  public addressSearchQuery = ''
  public mapTheme: string =
    process.env.REACT_APP_DEMO_STATUS === 'true' ? 'demoLight' : 'customLight'

  public deck: any
  public deckPrint: any
  private readonly appStore: AppStore
  private dropDownValues: any = {}

  public multiSelect: boolean = false
  public multiSelectIds = new Set<string>()

  public formData: FormData = new FormData()
  public sendReport = false
  public themeReport: any[] = []
  public isLoading = false
  public objectDeleted = false
  public componentRef: any

  constructor(appStore: AppStore) {
    this.appStore = appStore
    makeAutoObservable(this, {
      themeTree: false
    })

    this.handleSetQuarterLayer()

    // Address Search Side-Effect
    reaction(
      () => this.addressSearchQuery,
      (address) => {
        if (address.length < 3) {
          this.appStore.viewStore.addressSuggestion = []
          return
        }

        void this.appStore.selectedProject.client
          .get<Address[]>('/address/', { params: { search: address } })
          .then((res) => {
            this.appStore.viewStore.setAddressSuggestion(res.data)
          })
      },
      { delay: 1000 }
    )

    reaction(
      () => ({ themes: this.appStore.viewStore.selectedThemes.slice() }),
      ({ themes }) => {
        const tmp: any[] = []
        for (const themeElement of themes) {
          const theme =
            this.themeTree[themeElement.singleTheme][themeElement.subTheme]

          if (theme.legendColor) {
            const element = {
              type: theme.legendColor.type,
              value: theme.legendColor.values,
              id: themeElement.singleTheme + themeElement.subTheme,
              category: themeElement.subTheme,
              rgbColorUnit: theme.legendColor?.rgbColorUnit,
              singleTheme: themeElement.singleTheme,
              subTheme: themeElement.subTheme,
              aggregationLayerId: theme.layer.aggregationLayer,
              aggregationLayerLegendLabel:
                theme.layer.aggregationLayerLegendLabel,
              stateLayerLegend:
                theme.useStateLayerLegend !== undefined
                  ? this.appStore.selectedProject.geothermalWMS
                  : undefined
            }

            tmp.push(element)
          }
        }

        this.selectedThemesLegend = tmp
      }
    )
  }

  // Theme and Layer Selection
  handlePrintLayer = () => {
    if (!this.appStore.appStore.currentHistoryObject?.themes) {
      return
    }
    const keys = Object.keys(this.appStore.layerStore.buildingLayerList)
    this.reloadThemes(this.appStore.viewStore.selectedThemes)
    keys.forEach((key) => {
      if (
        this.appStore.viewStore.selectedThemes.find(
          (theme) => theme.layer[key] !== undefined
        )
      ) {
        this.appStore.layerStore.buildingLayerList[key].init = true
        this.appStore.layerStore.buildingLayerList[key].visible = true
      } else {
        this.appStore.layerStore.buildingLayerList[key].init = false
        this.appStore.layerStore.buildingLayerList[key].visible = false
      }
    })
  }

  setMultiSelect(multiSelect: boolean): void {
    if (this.multiSelect === multiSelect) return
    this.multiSelect = multiSelect
    if (!this.multiSelect && this.multiSelectIds.size > 1) {
      this.multiSelectIds = new Set()
      this.appStore.clearFocus()
    }
  }

  handleSetQuarterLayer = () => {
    const keys = Object.keys(this.appStore.layerStore.buildingLayerList)
    const quarterTheme = this.selectedThemesData.find(
      (theme) => theme.subTheme === 'Quartiere'
    )
    this.removeAllSelectedTheme()
    if (quarterTheme) this.addSelectedTheme(quarterTheme)
    this.reloadThemes(this.appStore.viewStore.selectedThemes)
    keys.forEach((key) => {
      if (key !== 'quarter' && key !== 'buildingsimulation') {
        this.appStore.layerStore.buildingLayerList[key].init = false
        this.appStore.layerStore.buildingLayerList[key].visible = false
      }
    })
  }

  addSelectedTheme(themeElement: ThemeElement) {
    if (!this.appStore.appStore.selectedProject.uuid) {
      return
    }
    for (const theme of this.appStore.viewStore.selectedThemes) {
      if (!themeElement.layer) {
        for (const layername of Object.keys(themeElement.layer.layer)) {
          if (layername in theme.layer.layer) {
            this.removeSelectedTheme(theme)
          }
        }
      }
    }

    this.appStore.viewStore.selectedThemes.push(themeElement)
  }

  removeSelectedTheme(themeElement: ThemeElement) {
    this.appStore.viewStore.selectedThemes =
      this.appStore.viewStore.selectedThemes.filter(
        (theme) =>
          theme.singleTheme !== themeElement.singleTheme ||
          theme.subTheme !== themeElement.subTheme
      )
  }

  removeAllSelectedTheme() {
    const tempSelectedThemes = this.appStore.viewStore.selectedThemes
    this.appStore.viewStore.selectedThemes = []
    this.selectedThemesLegend = []
    this.reloadThemes(tempSelectedThemes)
  }

  reloadThemes(previousSelection: ThemeElement[] = []) {
    if (
      this.appStore.appStore.selectedProject &&
      this.appStore.userStore.token
    ) {
      const deselectedThemes = previousSelection.filter(
        (e) =>
          !this.appStore.viewStore.selectedThemes.find(
            (el) => el.singleTheme + el.subTheme === e.singleTheme + e.subTheme
          )
      )

      for (const themeElement of deselectedThemes) {
        if (themeElement.subTheme === 'Versorgungsanlagen') {
          this.appStore.layerStore.setLayerVisibility(
            this.appStore.layerStore.buildingLayerList.IconSelect,
            false
          )
          this.appStore.layerStore.setLayerVisibility(
            this.appStore.layerStore.buildingLayerList.energyplant,
            false
          )
        }

        if (themeElement.subTheme === 'Quartiere') {
          this.appStore.layerStore.setLayerVisibility(
            this.appStore.layerStore.buildingLayerList.drawNewQuarter,
            false
          )
        }
        Object.keys(themeElement.layer).forEach((key) => {
          this.appStore.layerStore.setLayerVisibility(
            this.appStore.layerStore.buildingLayerList.heatmap,
            false
          )

          if (key === 'heatPotential') {
            this.appStore.layerStore.buildingLayerList.buildingsimulation.visible =
              false
          }

          const element =
            this.appStore.layerStore.buildingLayerList[
              themeElement.layer[key].type
            ]
          if (element) {
            element.visible = false
          }
        })
      }

      this.setCurrentHistoryThemes()

      for (const themeElement of this.appStore.viewStore.selectedThemes) {
        const theme =
          EnekaDefinitions.themes[themeElement.singleTheme][
            themeElement.subTheme
          ]
        for (const singleLayer of Object.keys(theme.layer)) {
          if (
            theme.layer[singleLayer].type &&
            theme.layer[singleLayer].type === 'energyplant'
          ) {
            this.appStore.layerStore.buildingLayerList.energyplant = {
              visible: true,
              singleLayer,
              themeElement,
              apivariable: theme.layer[singleLayer].apivariable,
              consumptionType: theme.layer[singleLayer].consumptionType
            }
          } else if (
            theme.layer[singleLayer].type &&
            theme.layer[singleLayer].type === 'marker'
          ) {
            this.appStore.layerStore.buildingLayerList.marker = {
              visible: true,
              singleLayer,
              themeElement,
              apivariable: theme.layer[singleLayer].apivariable
            }
            this.appStore.layerStore.buildingLayerList.heatmap = {
              visible: true,
              singleLayer,
              themeElement,
              apivariable: theme.layer[singleLayer].apivariable,
              aggregation: theme.zoom?.Heatmap,
              color: theme.layer[singleLayer].color
            }
          } else if (
            theme.layer[singleLayer].type &&
            theme.layer[singleLayer].type === FeatureType.Quarter
          ) {
            this.appStore.layerStore.buildingLayerList.quarter = {
              ...this.appStore.layerStore.buildingLayerList.quarter,
              visible: true,
              singleLayer,
              themeElement,
              apivariable: theme.layer[singleLayer].apivariable
            }
          } else if (theme.layer[singleLayer]?.type === 'geothermalLayer') {
            this.appStore.layerStore.buildingLayerList.geothermalLayer = {
              visible: true
            }
          } else if (
            theme.layer[singleLayer].type &&
            theme.layer[singleLayer].type === 'solartile'
          ) {
            this.appStore.layerStore.buildingLayerList.solartile = {
              visible: true,
              singleLayer,
              themeElement,
              apivariable: theme.layer[singleLayer].apivariable
            }
          } else if (
            theme.layer[singleLayer].type &&
            theme.layer[singleLayer].type === 'buildingsimulation'
          ) {
            this.appStore.layerStore.buildingLayerList.buildingsimulation = {
              ...this.appStore.layerStore.buildingLayerList.buildingsimulation,
              visible: true,
              singleLayer,
              themeElement,
              apivariable: theme.layer[singleLayer].apivariable
            }
            if (theme.layer[singleLayer].heatmap) {
              this.appStore.layerStore.buildingLayerList.heatmap = {
                visible: true,
                singleLayer,
                themeElement,
                apivariable: theme.layer[singleLayer].apivariable,
                aggregation: theme.zoom?.Heatmap,
                color: theme.layer[singleLayer].color
              }
            }
          } else if (
            theme.layer[singleLayer].type &&
            theme.layer[singleLayer].type === 'vegetationsimulation'
          ) {
            this.appStore.layerStore.buildingLayerList.vegetationsimulation = {
              ...this.appStore.layerStore.buildingLayerList
                .vegetationsimulation,
              visible: true,
              singleLayer,
              themeElement,
              apivariable: theme.layer[singleLayer].apivariable
            }
          } else if (singleLayer === 'heatPotential') {
            this.appStore.layerStore.buildingLayerList.buildingsimulation = {
              ...this.appStore.layerStore.buildingLayerList.buildingsimulation,
              visible: true,
              singleLayer,
              themeElement,
              apivariable: theme.layer[singleLayer].apivariable
            }
            if (theme.layer[singleLayer].heatmap) {
              this.appStore.layerStore.buildingLayerList.heatmap = {
                visible: true,
                singleLayer,
                themeElement,
                apivariable: theme.layer[singleLayer].apivariable,
                aggregation: theme.zoom?.Heatmap,
                color: theme.layer[singleLayer].color
              }
            }
            this.appStore.layerStore.buildingLayerList.heatPotential = {
              ...this.appStore.layerStore.buildingLayerList.heatPotential,
              visible: true,
              singleLayer,
              themeElement
            }
          }
        }
      }
    }
  }

  isThemeSelected(themeElement: ThemeElement) {
    return (
      this.appStore.viewStore.selectedThemes.findIndex(
        (o) =>
          o.singleTheme === themeElement.singleTheme &&
          o.subTheme === themeElement.subTheme
      ) !== -1
    )
  }

  // Quarter add, remove and draw logic
  drawNewQuarterAreaInMap({ color, uuid }) {
    const projectId = this.appStore.appStore.selectedProject.uuid
    apiCall({
      url: `/api/v1/project/${projectId}/quarter/${uuid}/?format=geojson`,
      accessToken: this.appStore.userStore.token?.access
    }).then((data) => {
      this.appStore.layerStore.setDrawNewQuarter({
        visible: true,
        data,
        color
      })
    })
  }

  async deleteQuarter(uuid: string, projectId?: string) {
    const id =
      projectId === undefined
        ? this.appStore.appStore.selectedProject.uuid
        : projectId || ''
    await apiCall({
      url: '/api/v1/project/' + id + '/quarter/' + uuid + '/',
      method: 'DELETE',
      accessToken: this.appStore.userStore.token?.access,
      responseType: ''
    }).then(
      action((res) => {
        this.appStore.monitorStore.objectResponse = res
        this.setApiResponse(
          res,
          'Gebiet erfolgreich gelöscht',
          'Fehler beim Löschen des Gebietes',
          200
        )
        this.objectDeleted = true
        this.appStore.bumpQuarterVersion()
      })
    )
  }

  handlequarterDrawMode() {
    this.toggleQuarterDrawMode()
    this.setLastObjectID('')
    if (this.appStore.viewStore.quarterDrawMode) {
      this.appStore.viewStore.showQuarterDrawMode = true
      this.appStore.layerStore.buildingLayerList.drawQuarter.visible = true
      this.appStore.layerStore.buildingLayerList.drawQuarter.init = true
    } else {
      this.appStore.viewStore.showQuarterDrawMode = false
      this.appStore.layerStore.buildingLayerList.drawQuarter.visible = false
      this.appStore.layerStore.buildingLayerList.drawQuarter.init = false
      this.appStore.viewStore.quarterAreaPoints = []
    }
  }

  async insertNewQuarter(
    quarterTitle: string,
    selectedColor: string,
    tagList: string[],
    id: string,
    coordinates: number[]
  ): Promise<QuarterResponse | any> {
    coordinates.push(coordinates[0])
    const wktCoordinates =
      this.appStore.viewStore.convertCoordinatesToWktGeometry(coordinates, {
        quarterTitle,
        type: 'Polygon'
      })
    const postObject = {
      title: quarterTitle,
      color: selectedColor,
      tags: tagList,
      geometry: `${wktCoordinatesSRID};${wktCoordinates}`
    }
    return await apiCall({
      url: `/api/v1/project/${id}/quarter/`,
      method: 'POST',
      body: JSON.stringify(postObject),
      accessToken: this.appStore.userStore.token?.access
    })
  }

  handleConsumptionDrawModus() {
    this.toggleConsumptionDrawMode()
    this.setLastObjectID('')
    if (this.appStore.viewStore.consumptionDrawModus) {
      this.appStore.viewStore.showConsumptionDrawMode = true
      this.appStore.layerStore.buildingLayerList.drawConsumption.visible = true
    } else {
      this.appStore.viewStore.showConsumptionDrawMode = false
      this.appStore.layerStore.buildingLayerList.drawConsumption.visible = false
      this.consumptionPoints = []
    }
  }

  async deleteEnergyplant(uuid: string, projectId?: string) {
    const id =
      projectId === undefined
        ? this.appStore.appStore.selectedProject.uuid
        : projectId || ''
    await apiCall({
      url: '/api/v1/project/' + id + '/energyplant/' + uuid,
      method: 'DELETE',
      accessToken: this.appStore.userStore.token?.access,
      responseType: ''
    }).then(
      action((res) => {
        this.appStore.monitorStore.objectResponse = res
        this.setApiResponse(
          res,
          'Versorgungsanlage erfolgreich gelöscht',
          'Fehler beim Löschen der Versorgungsanlage',
          200
        )
        this.objectDeleted = true
        this.appStore.bumpEnergyPlantVersion()
      })
    )
  }

  async insertNewConsumptionPoint(
    consumptionData: ConsumptionPointData,
    id: string
  ): Promise<any> {
    const data: any = consumptionData
    const wktCoordinatesNew = `SRID=4269;POINT(${consumptionData.coordinates?.[0].join(
      ' '
    )})`
    data.geometry = wktCoordinatesNew
    data.carriers = [Number(consumptionData.energyCarrier)]
    delete data.energyCarrier
    return await apiCall({
      url: `/api/v1/project/${id}/energyplant/`,
      body: JSON.stringify(data),
      method: 'POST',
      accessToken: this.appStore.userStore.token?.access
    }).then(async (res) => {
      const carrierData = {
        carrier: Number(data.carriers[0]),
        amount: Number(data.amount),
        unit: Number(data.unit)
      }
      return await apiCall({
        url: `/api/v1/project/${id}/energyplant/${res.uuid}/source/`,
        body: JSON.stringify(carrierData),
        method: 'POST',
        accessToken: this.appStore.userStore.token?.access
      }).then(() => {
        this.appStore.bumpEnergyPlantVersion()
        return res
      })
    })
  }

  addMultiSelectUuid(uuid: string): void {
    if (uuid === undefined) return
    if (this.multiSelectIds.has(uuid)) this.multiSelectIds.delete(uuid)
    else this.multiSelectIds.add(uuid)
  }

  // monitor object handle
  handleMapClick(e: PickInfo): void {
    if (this.appStore.viewStore.monitorVariablesChanged) {
      this.appStore.viewStore.setFeatureChangeOnEdit(true)
      return
    }
    const objectUuid = e.object?.properties?.uuid
    const layerType = e.layer?.type

    this.addCoordinate(e)

    // if there are already features in multiselection we ignore clicks on different object types
    if (this.appStore.focusType !== layerType && this.multiSelectIds.size > 0)
      return
    if (
      this.appStore.monitorStore.editMode &&
      this.appStore.viewStore.monitorVariablesChanged
    )
      return

    // if the clicked layer does not contain known features or no feature is hit just
    // clear the focus and return
    if (layerType === undefined) {
      this.appStore.clearFocus()
      return
    }

    if (
      this.multiSelect &&
      [
        FeatureType.Building,
        FeatureType.Vegetation,
        FeatureType.EnergyPlant
      ].includes(layerType)
    ) {
      if (
        this.multiSelectIds.size === 0 &&
        layerType === this.appStore.focusType
      )
        this.addMultiSelectUuid(this.appStore.featureFocus.uuid)

      this.addMultiSelectUuid(objectUuid)
      this.appStore.focusCollection(layerType, Array.from(this.multiSelectIds))
    } else if (objectUuid !== undefined) {
      if (layerType === FeatureType.Aggregation) {
        this.appStore.focusAggregation(objectUuid, e.object.properties)
      } else this.appStore.focus(layerType, objectUuid)
    }
  }

  addCoordinate = (e: any) => {
    if (this.appStore.viewStore.quarterDrawMode) {
      this.appStore.viewStore.quarterAreaPoints.push(e.coordinate)
      return
    }
    if (
      this.appStore.viewStore.consumptionDrawModus &&
      this.consumptionPoints.length === 0
    ) {
      const newCoordinates = { coordinates: [] }
      newCoordinates.coordinates = e.coordinate
      this.consumptionPoints.push(newCoordinates)
      this.appStore.layerStore.buildingLayerList.drawConsumption.visible = true
    }
  }

  // Report creation
  async createReportAsDocx(id, reportId, reportSlug, image, type: FeatureType) {
    const context = ['building', 'vegetation', 'energyplant'].includes(type)
      ? type + 'simulation'
      : type

    await apiCall({
      url:
        context === FeatureType.Project
          ? `/api/v1/project/${this.appStore.appStore.selectedProject.uuid}/report/${reportId}.docx`
          : `/api/v1/project/${this.appStore.appStore.selectedProject.uuid}/${context}/${id}/report/${reportId}.docx`,
      method: 'POST',
      responseType: 'docx',
      body: image,
      slug: reportSlug,
      headers: {
        Authorization: 'Bearer ' + this.appStore.userStore.token?.access
      }
    }).then((res) => {
      try {
        getDocumentAndDownload({
          blob: res,
          title: reportSlug,
          format: 'docx'
        })
      } catch (e) {}
    })
  }

  // Toggle
  toggleQuarterDrawMode = () => {
    this.appStore.viewStore.consumptionDrawModus = false
    this.appStore.viewStore.quarterDrawMode =
      !this.appStore.viewStore.quarterDrawMode
  }

  toggleConsumptionDrawMode = () => {
    this.appStore.viewStore.quarterDrawMode = false
    this.appStore.viewStore.consumptionDrawModus =
      !this.appStore.viewStore.consumptionDrawModus
  }

  toggleIsLoading = () => {
    this.isLoading = !this.isLoading
  }

  toggleQuarterClickHomepage = () => {
    this.appStore.viewStore.onQuarterClickHomepage =
      !this.appStore.viewStore.onQuarterClickHomepage
  }

  // Getter and Setter
  get selectedThemesData() {
    const themes: Array<{
      singleTheme: string
      subTheme: string
      layer: any
      dependencies: any
      useCase: string
    }> = []
    for (const singleTheme of Object.keys(this.themeTree)) {
      for (const subTheme of Object.keys(this.themeTree[singleTheme])) {
        for (const data of Object.keys(this.themeTree[singleTheme][subTheme])) {
          if (data === 'useCase') {
            const selectedTheme = {
              singleTheme,
              subTheme,
              layer: this.themeTree[singleTheme][subTheme].layer,
              dependencies: this.themeTree[singleTheme][subTheme].dependencies,
              useCase: this.themeTree[singleTheme][subTheme].useCase
            }
            themes.push(selectedTheme)
          }
        }
      }
    }
    return themes
  }

  get currentDependencies() {
    let tmp = { Allgemein: [], Wärme: [], Strom: [] }

    for (const theme of this.appStore.viewStore.selectedThemes) {
      if (tmp.Allgemein.length === 0) {
        tmp = { ...theme.dependencies }
      }

      for (const key of Object.keys(tmp)) {
        tmp[key] = tmp[key].filter((e: any) =>
          theme.dependencies[key].includes(e)
        )
      }
    }

    return tmp
  }

  async getDropDownValue(api: string) {
    if (!this.dropDownValues.hasOwnProperty(api)) {
      await apiCall({
        url: `/api/v1/${api}`,
        method: 'GET',
        accessToken: this.appStore.userStore.token?.access
      }).then((res) => (this.dropDownValues[api] = res))
    }
    return this.dropDownValues[api]
  }

  setCurrentHistoryThemes() {
    if (
      this.appStore.appStore.currentHistoryObject &&
      !this.appStore.appStore.fromHistorySelection &&
      ((this.appStore.appStore.currentHistoryObject.themes &&
        this.appStore.appStore.currentHistoryObject.themes.length !==
          this.appStore.viewStore.selectedThemes.length) ||
        !this.appStore.appStore.currentHistoryObject.themes)
    ) {
      this.appStore.appStore.currentHistoryObject.themes =
        this.appStore.viewStore.selectedThemes.map((theme) => {
          return {
            theme: theme.singleTheme,
            subTheme: theme.subTheme
          }
        })
    }
  }

  setIsLoading = (value: boolean) => {
    this.isLoading = value
  }

  setMapTheme(mapTheme: string): void {
    this.mapTheme = mapTheme
  }

  setSendReport(sendReport: boolean): void {
    this.sendReport = sendReport
  }

  setShowLegend(showLegend: boolean): void {
    this.appStore.viewStore.showLegend = showLegend
  }

  setAddressSearchQuery(value: string): void {
    this.addressSearchQuery = value
  }

  setObjectType(objectType: string): void {
    this.objectType = objectType
  }

  async setApiResponse(
    response,
    message,
    optionalText = undefined || '',
    errorCode,
    useExceptionCodesIfAvailable: boolean = true,
    fields: Form[] | undefined = undefined
  ) {
    const subMessages: string[] = []
    if (response.status && response.status <= 400 && response.status < 500) {
      try {
        const body = await response.json()
        if (
          body.hasOwnProperty('exception_codes') &&
          useExceptionCodesIfAvailable
        ) {
          for (const key of Object.keys(body.exception_codes)) {
            const formfield = fields
              ? fields.find((field) => field.apivariable === key)
              : undefined
            subMessages.push(
              formfield ? `${formfield.label} - ${body[key]}` : body[key]
            )
          }
        }
      } catch (error) {}
    }

    this.apiResponse = {
      responseCode: errorCode,
      message:
        !response.status ||
        response.status === 200 ||
        response.status === 201 ||
        response.status === 204 ||
        !optionalText
          ? message
          : optionalText,
      title: response.title
        ? response.title
        : response.statusText
          ? response.statusText
          : '',
      subMessages
    }
  }

  resetApiResponse(): void {
    this.apiResponse = undefined
  }

  setDeckPrint(deckPrint) {
    this.deckPrint = deckPrint
  }

  setDeck(deck) {
    this.deck = deck
  }

  setLastObjectID = (id: string) => {
    this.lastObjectID = id
  }
}

export default MapStore
